<script setup lang="ts">
import { DeviceDetailPage } from '@/components/DeviceComponents'
import DevicesOrganizationsAttachAction from '@/components/DeviceComponents/components/DeviceOrganizationsAttachAction.vue'
import DevicesOrganizationsDetachAction from '@/components/DeviceComponents/components/DeviceOrganizationsDetachAction.vue'
import StackLayout from '@/components/Layouts/StackLayout.vue'
import PermissionWrapper from '@/components/Permission/PermissionWrapper.vue'
import { ClickableTypography, TypographyItem, TypographyWithPrefix } from '@/components/Typography'
import { ADMIN_ORGANIZATIONS_ROUTES } from '@/router/routing/adminRouting/adminRoutes'
import deviceConfig from '@/services/deviceConfig.service'
import store from '@/store'
import { EDeviceActions } from '@/store/deviceStore/DeviceStoreTypes'
import { EStoreModules } from '@/store/storeType'
import type { EDeviceStatus } from '@/types/enum/DeviceStatusEnum'
import { defaultCreateDevice } from '@/types/models/DeviceCreateModel'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { DeviceTemplateKeysModel } from '@/types/models/DeviceTemplateKeysModel'
import { EAdminPerm } from '@/types/permissions/AdminPermissionsEnum'
import { EDevicesPerm } from '@/types/permissions/DevicesPermissionsEnum'
import { addToQuery } from '@/utils/queryUtils'
import { isEqual } from 'lodash'
import { computed, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import DeviceReinitializeFlag from '../../../components/DeviceComponents/Detail/InformationsTab/components/DeviceReinitializeFlag.vue'
import type { ExtendedPrimitives } from '../../../types/ExtendedPrimitives'
import DeleteDeviceAction from './components/DeleteDeviceAction.vue'
import DeviceApiKeyTypography from './components/DeviceApiKeyTypography.vue'
import DeviceSendCommandsAction from './components/DeviceSendCommandsAction.vue'
import DeviceTypeActionComponent from './components/DeviceTypeActionComponent.vue'

type Props = {
    device?: Partial<DeviceModel>
    tabsMultilined?: boolean
    isOutlookMode?: boolean
}
const props = defineProps<Props>()
type Emits = {
    (e: 'refresh'): void
    (e: 'refreshList'): void
}
const emit = defineEmits<Emits>()

const route = useRoute()
const deviceStore = ref(store.state.device.device)

const localDeviceId = computed(() => {
    if (route.query.preview) return parseInt(route.query.preview.toString())
    if (props.device && props.device.id) return props.device.device_id ?? props.device.id
    return route.params.device_id ? parseInt(route.params.device_id.toString()) : undefined
})

const data = ref<Partial<DeviceModel>>({ ...defaultCreateDevice })
const templateKeys = ref<DeviceTemplateKeysModel[]>([])
const getDeviceConfig = (deviceId: number) =>
    deviceConfig
        .getDeviceTypeConfigByVersion(deviceId)
        .then(res => (templateKeys.value = res.data?.properties ?? []))

const getDevice = (avoidRefreshQuery?: boolean) =>
    store
        .dispatch(`${EStoreModules.DEVICE}/${EDeviceActions.FETCH_DEVICE}`, {
            deviceId: localDeviceId.value,
            noDocumentTitle: props.isOutlookMode
        })
        .then(res => {
            data.value = { ...res }
            deviceStore.value = { ...res }
            templateKeys.value = []
            if (!avoidRefreshQuery && props.isOutlookMode)
                addToQuery({ preview: res.device_id ?? res.id })
            if (res.device_type_id && (res.device_id || res.id))
                return getDeviceConfig(res.device_id ?? res.id)
        })

watch(
    () => localDeviceId.value,
    (newDeviceId, oldDeviceId) => {
        if (newDeviceId && newDeviceId !== oldDeviceId) getDevice(true)
    },
    { immediate: true }
)

const save = () => {
    if (!isEqual(data.value, deviceStore.value))
        store
            .dispatch(`${EStoreModules.DEVICE}/${EDeviceActions.UPDATE_DEVICE}`, {
                deviceId: localDeviceId.value,
                deviceData: { ...data.value, roles: ['device'] }
            })
            .then(res => {
                data.value = { ...data.value, ...res }
                deviceStore.value = { ...data.value, ...res }
            })
}
const updateDeviceValue = (value: Partial<DeviceModel>) => (data.value = value)

const deviceStatus = computed(() => ({
    battery: props.device?.battery ?? deviceStore.value?.battery,
    plugged: props.device?.plugged ?? deviceStore.value?.plugged,
    signal: props.device?.signal ?? deviceStore.value?.signal,
    status: (props.device?.status as EDeviceStatus) ?? (deviceStore.value?.status as EDeviceStatus),
    network_type: props.device?.network_type ?? deviceStore.value?.network_type
}))

const handleActionConfirm = () => {
    emit('refresh')
    getDevice(true)
}

const handleDeleteRefresh = () => emit('refreshList')

const updateDeviceConfig = (config: Record<string, ExtendedPrimitives>) => {
    if (!deviceStore.value) return
    const deviceStoreConfig = { ...deviceStore.value.config }
    const dataConfig = { ...data.value.config }

    const configDifferences = Object.entries(dataConfig ?? {}).reduce(
        (prev, [itemKey, itemValue]) =>
            deviceStoreConfig[itemKey] === itemValue
                ? prev
                : { ...prev, [itemKey]: dataConfig[itemKey] ?? itemValue },
        {}
    )

    data.value.config = { ...dataConfig, ...config, ...configDifferences }
    deviceStore.value.config = { ...deviceStore.value.config, ...config }
}

provide('isAdmin', ref(true))
</script>

<template>
    <DeviceDetailPage
        :storedData="deviceStore"
        :data="data"
        :status="deviceStatus"
        :templateKeys="templateKeys"
        :editPermission="EAdminPerm.ADMIN_DEVICES"
        noDetectionLink
        @save="save"
        @update="updateDeviceValue"
        @configUpdate="updateDeviceConfig"
        :loading="store.state.device.isLoading">
        <template #informationsContent>
            <TypographyWithPrefix
                v-if="data.organization?.name && data.organization?.organization_id"
                :prefix="$t('device.model.organizationName')">
                <template #edit>
                    <ClickableTypography
                        v-if="data.organization?.name"
                        :label="data.organization?.name"
                        :href="
                            ADMIN_ORGANIZATIONS_ROUTES.buildUrl(data.organization?.organization_id)
                        " />
                    <TypographyItem v-else :label="$t('common.undefined')" />
                </template>
            </TypographyWithPrefix>

            <PermissionWrapper :permission="EDevicesPerm.DEVICES_API_KEY">
                <DeviceApiKeyTypography
                    v-if="data?.device_id || data?.id"
                    :deviceId="data?.device_id ?? data?.id" />
            </PermissionWrapper>
        </template>
        <template #actions>
            <StackLayout
                v-if="data.organization?.organization_id || localDeviceId"
                :gap="8"
                class="full-width">
                <DevicesOrganizationsAttachAction
                    :deviceId="localDeviceId"
                    @refresh="handleActionConfirm" />
                <DevicesOrganizationsDetachAction
                    v-if="data.organization?.organization_id"
                    :deviceId="localDeviceId"
                    :organizationId="data.organization?.organization_id"
                    :organizationName="data.organization.name ?? ''"
                    @refresh="handleActionConfirm" />
            </StackLayout>
            <DeviceReinitializeFlag :device="data" class="full-width" />
            <DeviceTypeActionComponent :data="data" @refresh="handleActionConfirm" />
            <DeviceSendCommandsAction
                v-if="data?.device_id || data?.id"
                :deviceId="data?.device_id ?? data.id!" />
            <DeleteDeviceAction
                v-if="data && (data.device_id || data.id)"
                :deviceId="data.device_id ?? data.id!"
                :name="data.name ?? data.serial_number ?? ''"
                @refresh="handleDeleteRefresh" />
        </template>
    </DeviceDetailPage>
</template>
